// Shared Variant Mapping read. One edge-function document feeds both the
// mapping rows and the variant catalog, so the link table is loaded once.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CloneMappingReadFilter } from '@/shared/variantMapping/cloneMappingCutoff';

export const VARIANT_MAPPING_WORKBENCH_QUERY_KEY = 'variant-mapping-workbench';

export function variantMappingWorkbenchQueryKey(organisationId: string | undefined) {
  return [VARIANT_MAPPING_WORKBENCH_QUERY_KEY, organisationId] as const;
}

export interface VariantMappingWorkbenchObservation {
  id: string;
  organisation_id: string;
  source_channel: string;
  ingestion_transport: string;
  source_detail?: string | null;
  interpreted_identifier_type: string | null;
  raw_source_label: string;
  raw_as_observed_value: string;
  description_text?: string | null;
  asserted_at?: string | null;
  created_at?: string | null;
  duplicate_of?: string | null;
}

export interface VariantMappingWorkbenchSuggestion {
  id: string;
  observation_id: string;
  status: string;
  candidates: unknown;
  created_at?: string | null;
}

export interface VariantMappingWorkbenchLink {
  id?: string;
  master_product_id: string;
  identifier_type: string;
  identifier_value: string;
  description_text?: string | null;
  created_at: string;
  active: boolean;
  resolution_source?: string | null;
}

export interface VariantMappingWorkbenchMasterProduct {
  id: string;
  organisation_id: string;
  eve_id: number;
  last_display_name?: string | null;
  last_identifier_value?: string | null;
  merged_into_id?: string | null;
}

export interface VariantMappingWorkbenchOrganisation {
  is_test_clone: boolean | null;
  created_at: string;
  data_cutoff_date: string | null;
}

export interface VariantMappingWorkbenchPayload {
  observations: VariantMappingWorkbenchObservation[];
  suggestions: VariantMappingWorkbenchSuggestion[];
  links: VariantMappingWorkbenchLink[];
  master_products: VariantMappingWorkbenchMasterProduct[];
  organisation: VariantMappingWorkbenchOrganisation | null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function parseVariantMappingWorkbench(data: unknown): VariantMappingWorkbenchPayload {
  if (!data || typeof data !== 'object') {
    throw new Error('Variant mapping workbench response was empty');
  }
  const record = data as Record<string, unknown>;
  const organisation = record.organisation;
  return {
    observations: asArray<VariantMappingWorkbenchObservation>(record.observations),
    suggestions: asArray<VariantMappingWorkbenchSuggestion>(record.suggestions),
    links: asArray<VariantMappingWorkbenchLink>(record.links),
    master_products: asArray<VariantMappingWorkbenchMasterProduct>(record.master_products),
    organisation:
      organisation && typeof organisation === 'object'
        ? (organisation as VariantMappingWorkbenchOrganisation)
        : null,
  };
}

export function cloneReadFilterFromWorkbench(
  organisation: VariantMappingWorkbenchOrganisation | null,
): CloneMappingReadFilter | undefined {
  if (!organisation?.is_test_clone || typeof organisation.created_at !== 'string') return undefined;
  return {
    orgCreatedAt: organisation.created_at,
    cutoffDate: organisation.data_cutoff_date ?? null,
  };
}

export async function fetchVariantMappingWorkbench(
  organisationId: string,
): Promise<VariantMappingWorkbenchPayload> {
  const { data, error } = await supabase.functions.invoke('load-variant-mapping-workbench-v1', {
    body: { organisationId },
  });
  if (error) throw error;
  return parseVariantMappingWorkbench(data);
}

const WORKBENCH_STALE_TIME_MS = 30_000;

export function useVariantMappingWorkbench(organisationId: string | undefined) {
  return useQuery({
    queryKey: variantMappingWorkbenchQueryKey(organisationId),
    enabled: !!organisationId,
    staleTime: WORKBENCH_STALE_TIME_MS,
    queryFn: () => fetchVariantMappingWorkbench(organisationId as string),
  });
}
